import { useFeatureAttachments } from "entity-app/hooks/feature-attachments";
import "./feature-attachments.scss";
import { Button, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import { getFileDownloadURL } from "services/file-mgmt";
import { downloadFileFromS3andOpenInNewTab } from "utils/utils";
import { useHistory, useParams } from "react-router";
import { useSubmmittialDetails } from "pages/submittal-details/new-submittal-details/hook/submittal-details-hook";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { useContext } from "react";
import AttachmentItem from "./attachment-item";
import { EntityType, ErrorMessages, noDocumentMessage } from "../../constants";

function resolveAuditFeatureType(
  featureType?: EntityType,
  auditFeatureType?: string
): string {
  if (auditFeatureType) return auditFeatureType;
  if (featureType === EntityType.Material) return "MATERIALS";
  if (featureType === EntityType.Submittal) return "SUBMITTALS";
  return "SUBMITTALS";
}

function FeatureAttachments(props: {
  isRevisionMode?: boolean;
  featureInstanceData?: any;
  featureId: string;
  disabled?: boolean;
  setTabIndex?: (tab: string) => void;
  featureType?: EntityType;
  /** Override for audit log featureType (e.g. DESIGN_PACKAGE, BID_PACKAGE). */
  auditFeatureType?: string;
  setAddMore?: any;
}) {
  const {
    isRevisionMode,
    featureInstanceData,
    featureId,
    disabled = false,
    setTabIndex,
    featureType,
    auditFeatureType,
    setAddMore
  } = props;
  const { attachments, loading } = useFeatureAttachments(
    featureInstanceData,
    featureInstanceData?.revision
  );
  const history = useHistory();
  const { projectId } = useParams() as any;
  const { gqlClientForProject }: TProjectContext = useContext(ProjectContext);
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  const { getAttachBtnDisabledMsg } = useSubmmittialDetails({
    featureInstanceData,
    isRevisionMode: isRevisionMode || false
  });

  const onAttachmentClick = async (data: any) => {
    message.success(ErrorMessages.comments.fileDownloadStarted);
    try {
      const fileDownloadURLResp: any = await getFileDownloadURL([
        data.blob_key
      ]);
      await downloadFileFromS3andOpenInNewTab(
        fileDownloadURLResp.data.success.urls[data.blob_key],
        data.file_type,
        data.file_name
      );
      saveAuditLogEvent({
        eventType: "FILE_ATTACHMENT_DOWNLOAD",
        featureType: resolveAuditFeatureType(featureType, auditFeatureType),
        filename: data.file_name || "",
        attachmentId: data.id
      });
    } catch (ex: any) {
      const errorMsg: string =
        ex.response?.data?.error ||
        ex.message ||
        "Unable to download attachment";
      message.error(errorMsg);
    }
  };

  if (loading && (!attachments || !attachments.length)) {
    return null;
  }

  const addMoreBtn = (
    <Tooltip title={getAttachBtnDisabledMsg()} placement="rightBottom">
      <Button
        size="small"
        type="text"
        className="border-none pr-1"
        onClick={() => {
          if (setTabIndex) {
            setTabIndex("2");
          }
          history.push(`/project/${projectId}/submittals/${featureId}?tab=2`);
          setAddMore(true);
        }}
        title={getAttachBtnDisabledMsg()}
        disabled={disabled}
        data-testid="index-button-add-more"
      >
        <span className="add-more-btn">Add More</span>
      </Button>
    </Tooltip>
  );

  if (attachments && !attachments.length) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 text-xs">
        <span className="text-xs text-neutral-400">{noDocumentMessage}</span>
        {!isRevisionMode && featureType === EntityType.Submittal && addMoreBtn}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="feature-attachments-root">
      <div className="flex w-full justify-between">
        {!isRevisionMode &&
          featureType === EntityType.Submittal &&
          attachments &&
          attachments.length > 0 &&
          addMoreBtn}
      </div>
      {attachments?.map((attachment: any) => (
        <AttachmentItem
          key={attachment.id}
          attachment={attachment}
          onClick={onAttachmentClick}
          data-testid="feature-attachments-item"
        />
      ))}
    </div>
  );
}

export default FeatureAttachments;
